import {Storage} from '@ionic/storage';
import {Injectable} from '@angular/core';
import {SecureStorage, SecureStorageObject} from '@ionic-native/secure-storage';
import {Platform } from 'ionic-angular';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { StorageType } from '../../model/types/storage-type';
import { TranslateService } from '@ngx-translate/core';
import { RouteProvider } from '../../providers/route/route';
import { AnalyticsEventType, ScreenLockSelection } from '../../model/types/analytics.const';
import { userTokenRef } from '../../model/myria/app/user';
/**
 * @description Used for to handle local storage
 */
@Injectable()
export class StorageProvider {

  static initStorage: boolean = false;
  static pendingStorage: Promise<boolean>;
  static secureStorage: SecureStorageObject;
  static secureStorageNeedsSetup: boolean = false;
  static secureStorageSkip: boolean = false;
  static secureStorageWorked: boolean = false;
  static secureStorageLock: Promise<boolean>;
  static storageSkip: boolean = false;
  private static readonly FIRST_RUN_INDICATOR = 'first_run_indicator';
  static temporaryStorage = {};
  static analytics: any = null;

  constructor(
    private storage: Storage,
    private translate: TranslateService,
    private secureStorageProvider: SecureStorage,
    private platform: Platform,
    private sanitizer: DomSanitizer,
    private routeProvider: RouteProvider,
  ) {    
    if (!StorageProvider.initStorage) {      
      StorageProvider.initStorage = true;
      StorageProvider.pendingStorage = new Promise((resolve) => {
        platform.ready()
        .then(() => {
          this.init(false, resolve);
        });
      });
    }
  }
  
  public async resetStorage() {
    try {
      LoggerProvider.info(this, 'Reset Local Storage - Username changed');
      await this.storage.clear();
      StorageProvider.initStorage = true;
      StorageProvider.pendingStorage = new Promise((resolve) => {
        this.platform.ready()
          .then(() => {
            this.init(false, resolve);
          });
      });
      this.set(StorageType.ConsentIDFA, 'false');
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Error while resetting local storage');
    }
  }

  private async init(showScreenIfNeeded: boolean, resolve = null) {
    this.storage.ready();

    try {
      const skip = await this.storage.get(StorageType.SkipScreenLockCheck);
      if (skip === '1') {
        LoggerProvider.warning(this, LoggerEvent.StorageException, 'Skip screen lock check');
        StorageProvider.secureStorageSkip = true;
        StorageProvider.secureStorageNeedsSetup = false;
        StorageProvider.pendingStorage = null;        
        if (resolve)
          resolve(true);
        return;
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.StorageException, 'Failed to check if screen lock check should be skipped');
    }

    if (this.platform.is('cordova')){
      this.secureStorageProvider.create('rmt_storage')
        .then((storage) => {
          LoggerProvider.info(this, 'secure storage created');
          this.finishInit(storage, resolve);
        })
        .catch((ex) => {
          LoggerProvider.exception(this, ex, LoggerEvent.StorageException, 'failed to init secure storage');

          // it fails when no screen lock has been set, so let's redirect them to the screen lock settings
          StorageProvider.secureStorageNeedsSetup = true;
          if (showScreenIfNeeded) {
            this.showScreenLockSettings(resolve);
          }
          else {
            resolve(false);
          }
        });        
    } else {
      StorageProvider.secureStorageNeedsSetup = false;
      if (resolve) {
        resolve(true);
      }
    }
  }

  private async readSecureKey(storage: SecureStorageObject, key: string): Promise<string> {
    try {
      return await storage.get(key);
    } catch (ex) {
      return null;
    }
  }

  private async readDeviceTrustEntries(storage: SecureStorageObject): Promise<{ key: string, value: string }[]> {
    const entries = [];
    try {
      const keys = await storage.keys();
      for (const key of keys || []) {
        if (key.indexOf(StorageType.DeviceTrustToken) !== 0)
          continue;
        const value = await this.readSecureKey(storage, key);
        if (value)
          entries.push({ key: key, value: value });
      }
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.StorageException, 'Failed to read device trust tokens before clearing secure storage', ex);
    }
    return entries;
  }

  private async restoreDeviceTrustEntries(storage: SecureStorageObject, entries: { key: string, value: string }[]) {
    for (const entry of entries) {
      try {
        await storage.set(entry.key, entry.value);
      } catch (ex) {
        LoggerProvider.warning(this, LoggerEvent.StorageException, 'Failed to preserve device trust token', ex);
      }
    }
  }

  public async clearFirstRunIndicator(): Promise<void> {
    await this.storage.remove(StorageProvider.FIRST_RUN_INDICATOR);
  }

  async finishInit(storage, resolve) {
    // the secure storage doesn't get cleared after reinstall on iOS, hence check if it's the first run and eventually clear it
    const key = StorageProvider.FIRST_RUN_INDICATOR;
    try {
      const value = await this.storage.get(key);
      if (!value) {
        LoggerProvider.info(this, 'Clear secure storage');
        const deviceTrustEntries = await this.readDeviceTrustEntries(storage);
        await storage.clear();
        storage = await this.secureStorageProvider.create('rmt_storage'); // recreate the storage as the clear will delete it on Android
        await this.restoreDeviceTrustEntries(storage, deviceTrustEntries);
      }
    }
    catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.StorageException, 'failed to check if secure storage needs to be cleared');
    }
    this.storage.set(key, '1');

    StorageProvider.secureStorageNeedsSetup = false;
    StorageProvider.secureStorage = storage;
    StorageProvider.pendingStorage = null;
    if (resolve) {
      resolve(true);
    }
    LoggerProvider.info(this, 'secure storage initialized');
  }

  public checkScreenLockSettings() {
    if (StorageProvider.secureStorageNeedsSetup) {
      LoggerProvider.info(this, 'Show screen lock settings');
      this.showScreenLockSettings();
    }
  }

  public showScreenLockSettings(resolve = null) {
    // unfortunately the ionic native wrapper for the cordova plugin secure storage has a small quirk, so you are not able to call secureDevice if it failed
    // hence we access the cordova plugin directly to call secureDevice to access the lock screen settings    
    const storage = new window['SecureStorage'](
      () => {},
      () => {
        this.routeProvider.openModal(this.routeProvider.pages.ModalAlertPage, {
          title: this.translate.instant('global.screenLockContainer.screenLockHeader'),
          image: 'assets/imgs/lock-with-key.svg',
          subtitle: this.translate.instant('global.screenLockContainer.screenLockContent'),
          primaryButtonText: this.translate.instant('global.screenLockContainer.enableScreenLockButton'),
          secondaryButtonText: this.translate.instant('global.screenLockContainer.disableScreenLockButton'),
          showSecondaryButton: true,
          primaryAction: () => this.enableScreenLock(storage, resolve),
          secondaryAction: () => this.setSkipScreenLockSetting(resolve),
          showHelp:false,
          showBack:false,
          showClose: false,
          goToActivity: false,
          showFullPage: true,
        }, null);
      });
  }

  public async getJson(key: string): Promise<any> {    
    let value = await this.get(key);
    try {
      value = JSON.parse(value);
    }
    catch (ex) {
      value = null;
    }
    return value;
  }

  public setJson(key: string, value: any): Promise<boolean> {
    return this.set(key, JSON.stringify(value));
  }

  /**
   *
   * @param key
   * @param value (ensure string as type otherwise set the secureStorage may fail due to incompatible type i.e. number)
   * @returns {Promise<boolean>}
   */
  public async set(key: string, value: string): Promise<boolean> {
    if (value === null) value = '';
    LoggerProvider.info(this, 'Set storage key ' + key);
    const promise = new Promise((resolve, reject) => {      
      if (StorageProvider.pendingStorage) {        
        StorageProvider.pendingStorage.then(() => {
          this.processSet(key, value, resolve, reject);
        });
      }
      else {
        this.processSet(key, value, resolve, reject);
      }
    });
    let success = false;
    try {
      const result = await promise;
      if (result)
        success = true;
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.StorageException, 'Exception during saving key \"' + key +'\" to Secure Storage', ex);
    }
    return success;
  }
  
  private async processSet(key: string, value: string, resolve, reject) {    
    if(StorageProvider.secureStorage && !StorageProvider.secureStorageSkip) {
      let result: any = false;
      const lockFinished = await this.lockSecureStorage();
      try {
        result = await new Promise((promiseResolve) => {
          StorageProvider.secureStorage.set(key, value)
          .then(() => {
            StorageProvider.secureStorageWorked = true; // if secure storage worked before remember it so we keep it on
            LoggerProvider.info(this, 'Successfully set secure storage key ' + key);
            promiseResolve(true)
          })
          .catch((err) => {
            if (StorageProvider.secureStorageWorked) {
              LoggerProvider.warning(this, LoggerEvent.StorageException, 'Saving key \"' + key +'\" value to Secure Storage failed', err);
            } else {
              // only disable the secure storage if it hasn't worked before otherwise we risk to deactivate it just when we got an exception
              StorageProvider.secureStorageSkip = true; // don't spam us with warnings
              LoggerProvider.warning(this, LoggerEvent.StorageException, 'Saving key \"' + key +'\" value to Secure Storage failed - disabling Secure Storage', err);
            }
            promiseResolve(false);
          });
        });
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Exception during processSet for secure storage');
      }
      lockFinished(true);

      if (result) {
        resolve();
        return;
      }
    }

    // only allow fallback for important unrisky values
    // DON'T STORE AUTH TOKEN OR ORDERS !
    if (!StorageProvider.storageSkip) {
      if (!this.platform.is('cordova') ||
          key === StorageType.LastLogin || key === StorageType.LastLoginCountry || key === StorageType.LastLoginFirstName || key === StorageType.Language || key === StorageType.UserCountry || key === StorageType.ConsentIDFA ||
          key === StorageType.ConsentIDFAPermission || key === StorageType.ConsentIDFARetried || key === StorageType.ConsentIDFAReasked ||
          key === StorageType.HiddenMenuFeatures || key === StorageType.HiddenMenuLastAppliedFeatures ||
          key === StorageType.Rmt5FlagMode || key === StorageType.FeatureFlagOverrides || key === StorageType.LastDeviceLanguage) {
        const result = await new Promise((promiseResolve) => {
          this.storage.set(key, value)
          .then(() => {
            LoggerProvider.info(this, 'Successfully set storage key ' + key);
            promiseResolve(true)
          })
          .catch((err) => {
            StorageProvider.storageSkip = true; // don't spam us with warnings
            LoggerProvider.warning(this, LoggerEvent.StorageException, 'Saving key \"' + key +'\" value to Storage failed - disabling Storage', err);
            promiseResolve(false);
          });
        });
        if (result) {
          resolve();
          return;
        }
      }
    }

    // in worst case fallback to just temporary store the values so the app works
    LoggerProvider.info(this, 'Successfully set session storage key ' + key);
    StorageProvider.temporaryStorage[key] = value;
    resolve();
  }
  
  /**
   *
   * @param key
   * @returns {Promise<string>}
   */
  public async get(key: string): Promise<string> {          
    LoggerProvider.info(this, 'Get storage key ' + key);
    const promise = new Promise((resolve) => {      
      if (StorageProvider.pendingStorage) {        
        StorageProvider.pendingStorage.then(() => {
          this.processGet(key, resolve);
        });
      }
      else {
        this.processGet(key, resolve);
      }
    });

    let result = null;
    try {
      result = await promise;
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.StorageException, 'Exception during getting key \"' + key +'\" from Secure Storage', ex);
    }
    return result;
  }

  private processData(data, resolve) {
    if (data === null) {
      resolve(null);
    }
    else {
      resolve(data);
    }
  }
  
  private async processGet(key: string, resolve) {
    if (StorageProvider.temporaryStorage[key]) {
      LoggerProvider.info(this, 'Successfully got session storage key ' + key);
      resolve(StorageProvider.temporaryStorage[key]);
      return;    
    }

    if(StorageProvider.secureStorage && !StorageProvider.secureStorageSkip) {      
      const lockFinished = await this.lockSecureStorage();
      try {
        StorageProvider.secureStorage.get(key)
          .then((data) => {
            lockFinished(true);
            LoggerProvider.info(this, 'Successfully got secure storage key ' + key);    
            this.processData(data, resolve);
          })
          .catch(() => {
            lockFinished(false);
            resolve(null)
          });
      } catch (ex) {
        lockFinished(false);
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Exception during processGet for secure storage');
      }
    }
    else if (!StorageProvider.storageSkip) {
      this.storage.get(key)
        .then((data) => {
          LoggerProvider.info(this, 'Successfully got storage key ' + key);    
          this.processData(data, resolve);
        })
        .catch(() => resolve(null));
    }
    else
      resolve(null);
  }

  public getImage(key): Promise<string | SafeUrl> {
    return this.get(key)
        .then((image) => {
          if (image.indexOf('!') === 0) {
            image = image.substr(1);
            const safeimage = this.sanitizer.bypassSecurityTrustUrl(image);
            return safeimage;
          }
          else {
            return image;
          }
        })
        .catch((err) => {
          throw err;
        });
  }

  /**
   *
   * @param key
   * @returns {Promise<boolean>}
   */
  public async remove(key): Promise<boolean> {
    LoggerProvider.info(this, 'Remove storage key ' + key);
    const promise = new Promise((resolve, reject) => {
      if (StorageProvider.pendingStorage) {
        StorageProvider.pendingStorage.then(() => {
          this.processRemove(key, resolve, reject);
        });
      }
      else {
        this.processRemove(key, resolve, reject);
      }
    });
    let success = false;
    try {
      const result = await promise;
      if (result)
        success = true;
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.StorageException, 'Exception during removing key \"' + key +'\" from Secure Storage', ex);
    }
    return success;
  }
  
  private async processRemove(key, resolve, reject) {
    if (StorageProvider.temporaryStorage[key]) {
      LoggerProvider.info(this, 'Successfully removed session storage key ' + key);
      delete StorageProvider.temporaryStorage[key];
      resolve(true);
      return;    
    }

    if(StorageProvider.secureStorage && !StorageProvider.secureStorageSkip) {
      const lockFinished = await this.lockSecureStorage();
      try {
        StorageProvider.secureStorage.remove(key)
          .then(() => {
            lockFinished(true);
            LoggerProvider.info(this, 'Successfully removed secure storage key ' + key);
            resolve(true)
          })
          .catch(() => {
            lockFinished(false);
            reject(false)
          });
      } catch (ex) {
        lockFinished(false);
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Exception during processRemove for secure storage');
      }
    } else if (!StorageProvider.storageSkip) {
      this.storage.remove(key)
        .then(() => {
          LoggerProvider.info(this, 'Successfully removed storage key ' + key);
          resolve(true)
        })
        .catch(() => reject(false));
    } else
      resolve(false);
  }

  setSkipScreenLockSetting(resolve){
    try {
      this.logScreenLockPromptedEvent(ScreenLockSelection.DontUse);
      this.storage.set(StorageType.SkipScreenLockCheck, '1');
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.StorageException, 'Failed to set skip screen lock check');
    }
    LoggerProvider.warning(this, LoggerEvent.StorageException, 'Skip screen lock');
    StorageProvider.secureStorageSkip = true;
    StorageProvider.secureStorageNeedsSetup = false;
    StorageProvider.pendingStorage = null;
    if (resolve)
      resolve(true);
  }

  enableScreenLock(storage, resolve){
    this.logScreenLockPromptedEvent(ScreenLockSelection.TurnOn);
    LoggerProvider.info(this, 'Enable screen lock');
    storage.secureDevice(
      () => {
        LoggerProvider.info(this, 'Screen lock enabled');
        this.init(true, resolve);
      },
      (ex) => {
        LoggerProvider.info(this, 'Screen lock still not enabled', ex);
        this.showScreenLockSettings(resolve); // if screen lock has not been set, show the dialog again
      }
    );
  }

  logScreenLockPromptedEvent(screenLockSelection){
    if (StorageProvider.analytics)
      StorageProvider.analytics.trackEvent({eventName: AnalyticsEventType.ScreenLockPrompted, analyticsData: {screenLockSelection: screenLockSelection}});
  }

  // it seems the secure storage can get into racing conditions when several operations are done at the same time and then cause exceptions - hence let's lock it to use it in a synchronous way
  async lockSecureStorage() {
    const promise = StorageProvider.secureStorageLock;
    let promiseResolve;

    // create a new promise then wait for the old one so promises can get kind of queued
    StorageProvider.secureStorageLock = new Promise((resolve) => {
      promiseResolve = resolve;
    });
    if (promise) {
      // let's be paranoid - if somehow the promise won't resolve (i.e. the plugin just gets stuck) we should still be able to continue hence have a timeout of 2 seconds
      await new Promise(async(resolve) => {
        let finished = false;
        let timeout = window.setTimeout(() => {
          resolve(true);
          finished = true;
          LoggerProvider.info(this, 'Had to cancel secure storage lock wait');

        }, 2000);
        await promise;
        window.clearTimeout(timeout);
        if (!finished)
          resolve(true);
      });
    }

    return promiseResolve;
  }

  async getSetUserTokenReference(isSetTokenRef: boolean, userTokenRef: userTokenRef = { userName: [], token: ''}){
    try{
      let userTokenObj: any = '';
      // Fill userTokenRef with last login username token
      if(!isSetTokenRef && userTokenRef.userName.length === 0) {
        let username = await this.get(StorageType.LastLogin);
        username = username.replace("-", "") || '';
        userTokenRef['userName'] = [username];
        userTokenRef['token'] = '';
      }
  
      if(userTokenRef.userName.length > 0){
        let toknRefDetail: any[] = JSON.parse(await this.get(StorageType.TokenReference)) || [];
        if(toknRefDetail && toknRefDetail.length > 0){
          toknRefDetail.forEach(result => {
            result.userName.forEach(user => {
              if(userTokenRef.userName[0] === user) userTokenObj = result;
            });
           })
        }
        if(isSetTokenRef) {
          if(userTokenObj && userTokenRef.token){
            toknRefDetail.map((result: userTokenRef) => {
              if(result.userName.includes(userTokenRef.userName[0])){
                result.token = userTokenRef.token;
                this.set(StorageType.TokenReference, JSON.stringify(toknRefDetail));  
              }
            })
          } else{
            toknRefDetail.push(userTokenRef);
            this.set(StorageType.TokenReference, JSON.stringify(toknRefDetail));
          }
        } else return userTokenObj ? userTokenObj.token : '';
      } else return userTokenObj ? userTokenObj.token : '';
    } catch(err){
      LoggerProvider.exception(this, err, LoggerEvent.setLoginToken, 'Error in get Set User TokenReference while login');
      return '';
    }
  }

  async deleteUserTokenRefFromLocalStorege(username){
    try {
      const data = await this.get(StorageType.TokenReference);
      let toknRefDetail: any[] = JSON.parse(data) || [];
      if(username && toknRefDetail && toknRefDetail.length > 0){
        for(var i=0;i <= toknRefDetail.length; i++){
          if(toknRefDetail[i].userName.includes(username)){
            toknRefDetail.splice(i,1)
            break;
          }
        }
        this.set(StorageType.TokenReference, JSON.stringify(toknRefDetail));
      }
    } catch(err){
      LoggerProvider.exception(this, err, LoggerEvent.setLoginToken, 'Error deleting token reference from local storage');
    }
  }
  
}


// WEBPACK FOOTER //
// ./src/providers/storage/storage.ts